/**
 * Bridge Login Page
 *
 * Displayed in the device browser when login is triggered from a webview.
 * Handles Web3Auth authentication, then bridges tokens back to the webview
 * via the backend session bridge.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBridgeLoginPageViewModel } from '../hooks/useBridgeLoginPageViewModel';
import { ludokaLogo } from '@/assets';
import { PillButton } from '@/shared/components/PillButton';
import { createResponsiveTextStroke } from '@/styles/textStroke';

export default function BridgeLoginPage() {
  const { t } = useTranslation();
  const { state, actions, onMount } = useBridgeLoginPageViewModel();

  useEffect(onMount, [onMount]);

  return (
    <div className="flex-1 w-full relative flex items-center justify-center">
      {/* Center content - Logo */}
      <img src={ludokaLogo} alt="Ludoka Logo" className="max-w-full max-h-full object-contain" />

      {/* Bottom - Content */}
      <div className="absolute bottom-40 left-1/2 -translate-x-1/2 w-full max-w-[280px] px-4">
        <h2
          className="font-mind-boggle text-[#F2F2F2] text-4xl text-center mb-4"
          style={createResponsiveTextStroke(2, 0.08)}
        >
          {t('login.connect')}
        </h2>

        {/* Error state */}
        {state.error && state.status === 'error' && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg">
            <p className="text-red-400 text-sm text-center">{state.error}</p>
          </div>
        )}

        {/* Success state — auto-returns to the originating widget */}
        {state.status === 'success' && (
          <div className="flex flex-col gap-4 items-center">
            <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg">
              <p className="text-green-400 text-base text-center font-semibold mb-2">
                {t('login.bridgeSuccessTitle')}
              </p>
              <p className="text-white/80 text-sm text-center">{t('login.bridgeReturning')}</p>
            </div>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Authenticating/Completing state */}
        {(state.status === 'authenticating' || state.status === 'completing') && (
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <p className="text-white text-sm">
              {state.status === 'authenticating'
                ? t('login.authenticating')
                : t('login.completingBridge')}
            </p>
          </div>
        )}

        {/* Ready state - show instruction and login buttons */}
        {(state.status === 'ready' || state.status === 'error') && (
          <div className="flex flex-col gap-3">
            <p className="text-white/80 text-sm text-center mb-1">{t('login.bridgeTapToLogin')}</p>
            <PillButton variant="blue" onClick={actions.handleFacebookLogin} className="h-14">
              FACEBOOK
            </PillButton>
            <PillButton variant="red" onClick={actions.handleGoogleLogin} className="h-14">
              GOOGLE
            </PillButton>
          </div>
        )}
      </div>
    </div>
  );
}
